import React, { useRef } from 'react';
import { useGSAP } from '@gsap/react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const KeyDifferentiators = () => {
  const sectionRef = useRef(null);

  useGSAP(() => {
    gsap.fromTo(sectionRef.current.querySelector('.hero-headline'),
      { y: 60, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 1.2,
        ease: "power4.out",
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 80%",
        }
      }
    );

    gsap.fromTo(sectionRef.current.querySelectorAll('.differentiator-card'),
      { y: 40, opacity: 0 }, 
      {
        y: 0,
        opacity: 1,
        duration: 0.8, 
        ease: "expo.out",
        stagger: 0.2, 
        scrollTrigger: {
          trigger: sectionRef.current.querySelector('.differentiators-container'),
          start: "top 90%", 
        }
      }
    );
  });

  const differentiators = [
    {
      id: 1,
      title: "Data-First Strategy",
      description: "Turn your data into a strategic weapon with AI models that learn, adapt, and optimize continuously.",
      image: "/images/data_strategy.png"
    },
    {
      id: 2,
      title: "Tailored AI Solutions",
      description: "Custom-built AI that fits your exact business model, not one-size-fits-all platforms.",
      image: "/images/ai_solution.png"
    },
    {
      id: 3,
      title: "Continuous Innovation",
      description: "Stay ahead with AI solutions that evolve with emerging technologies and market demands.",
      image: "/images/innovation.png"
    }
  ];

  return (
    <section ref={sectionRef} className="py-24 px-6 bg-[#0a050a] text-white overflow-hidden relative">
      {/* Dynamic Background Glows */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-[#952877]/10 blur-[120px] rounded-full pointer-events-none"></div>
      
      <div className="max-w-7xl mx-auto text-center relative z-10">
        {/* Headline Section */}
        <div className="mb-20">
          <h2 className="hero-headline text-4xl md:text-6xl font-black mb-8 leading-tight tracking-tight">
            While Your Competitors<br/>
            <span className="bg-gradient-to-r from-[#952877] to-[#592aa7] bg-clip-text text-transparent italic">
              Struggle with Yesterday's Tools
            </span><br/>
            We're Building Your AI-Powered Future
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto font-light">
            The difference isn't just technology—it's a strategic advantage that <span className="text-white">compounds over time</span>.
          </p>
        </div>

        {/* 3-Column Grid */}
        <div className="differentiators-container grid md:grid-cols-3 gap-8">
          {differentiators.map((item) => (
            <div 
              key={item.id} 
              className="differentiator-card group relative p-10 rounded-[40px] bg-[#17091a]/40 border-2  border-white  hover:border-[#952877]/90 transition-all duration-700 text-left backdrop-blur-xl  shadow-[0_0_15px_rgba(255,255,255,0.05)]">
              {/* Hover Radial Gradient */}
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(149,40,119,0.15),transparent_70%)] opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>

              {/* Icon Container */}
              <div className="w-20 h-20 mb-10 rounded-3xl bg-[#17091a] border border-white/5 flex items-center justify-center p-4 shadow-2xl group-hover:scale-110 group-hover:border-[#952877]/30 transition-all duration-500">
                <img 
                  src={item.image}
                  className="w-full h-full invert object-contain brightness-125 grayscale group-hover:grayscale-0 transition-all duration-500"
                  alt={item.title}
                />
              </div>

              {/* Content */}
              <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-[#952877] transition-colors duration-300">
                {item.title}
              </h3>
              <p className="text-gray-400 leading-relaxed font-light group-hover:text-gray-200 transition-colors duration-300">
                {item.description}
              </p>
              
              {/* Bottom Decorative Element */}
              <div className="mt-8 flex gap-1">
                <div className="h-1 w-8 bg-[#952877] rounded-full"></div>
                <div className="h-1 w-2 bg-white/20 rounded-full group-hover:bg-[#592aa7] transition-colors"></div>
                <div className="h-1 w-2 bg-white/10 rounded-full"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default KeyDifferentiators;